import { CustomSelect } from "../../../components/CustomSelect";
import { TEXT_FONT_OPTIONS } from "../../../lib/fontOptions";

/**
 * Edición de las tipografías de la marca (tab Visual): un `select` por rol
 * — [0] principal, [1] secundaria, el mismo orden que ya usa
 * `brand_profiles.fonts.suggested`. Vive aparte de `MarcaVisualPanel` para no
 * pasarnos del tamaño de archivo recomendado.
 *
 * Se usa `CustomSelect` (el mismo que "Mi diseño") y no un `<select>` nativo
 * porque el menú se portalea a `<body>`: el panel de contenido de la app tiene
 * `clip-path` y recorta a cualquier descendiente, incluso `fixed`.
 *
 * El componente NO guarda: solo levanta el borrador.
 */

interface EditorTipografiasMarcaProps {
  /** Borrador: `[principal, secundaria]`. Un rol vacío se representa con "". */
  familias: string[];
  /** Familias guardadas hoy en la marca, para no perderlas del desplegable. */
  familiasOriginales: string[];
  onCambiar: (familias: string[]) => void;
  deshabilitado?: boolean;
}

const ROLES = ["Principal", "Secundaria"];

export const EditorTipografiasMarca = ({
  familias,
  familiasOriginales,
  onCambiar,
  deshabilitado = false,
}: EditorTipografiasMarcaProps) => {
  // Las 17 familias curadas de la app + las propias de la marca. Sin estas
  // últimas, una marca con una fuente que no está en la lista (ej. la que le
  // asignó la IA en el onboarding) no podría volver a elegirla después de
  // cambiarla, y el `select` arrancaría en blanco. Mismo criterio que
  // `fontFamilyOptions` en `CompanyData.tsx`.
  const vistas = new Set<string>();
  const opciones: { value: string; label: string }[] = [];
  const sumar = (familia: string, etiqueta?: string) => {
    const limpia = (familia ?? "").trim();
    if (!limpia || vistas.has(limpia.toLowerCase())) return;
    vistas.add(limpia.toLowerCase());
    opciones.push({ value: limpia, label: etiqueta || limpia });
  };
  TEXT_FONT_OPTIONS.forEach((o) => sumar(o.family, o.label));
  [...familiasOriginales, ...familias].forEach((f) => sumar(f));

  return (
    <div className="mt-3 grid max-w-md grid-cols-2 gap-4">
      {ROLES.map((rol, indice) => {
        const familia = familias[indice] ?? "";
        return (
          <div
            key={rol}
            className="rounded-xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] p-4 text-center"
          >
            <p
              className="text-[32px] leading-none text-brand-black"
              style={familia ? { fontFamily: `"${familia}", sans-serif` } : undefined}
            >
              Aa
            </p>
            <p className="mt-2 text-[10px] uppercase tracking-wide text-[rgb(var(--brand-black-rgb)_/_0.4)]">{rol}</p>
            <div className="mt-2">
              <CustomSelect
                options={opciones}
                value={familia}
                onChange={(valor) =>
                  onCambiar(ROLES.map((_, i) => (i === indice ? valor : familias[i] ?? "")))
                }
                placeholder="Sin elegir"
                disabled={deshabilitado}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EditorTipografiasMarca;
